import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import TradeTiles from '../TradeTiles/TradeTiles';
import {
  buildTaskStepPath,
  buildTimingStepPath,
  buildTradeStepPath,
  searchServiceCatalog,
} from '../../utils/serviceSearch';
import {
  SearchBarRoot,
  SearchFieldRow,
  SearchHeading,
  SearchInput,
  SearchSubheading,
  SearchSubmitButton,
  SuggestionIcon,
  SuggestionLabel,
  SuggestionOption,
  SuggestionTrade,
} from './ServiceSearchBar.styled';

/**
 * Service search entry: "What do you need help with?" with catalog typeahead, plus trade tiles.
 * Picking a task jumps to the timing step; unmatched text goes to the
 * trade picker with the text kept as a note. The `hero` variant is just the field, for placing
 * over a photo hero that supplies its own heading and trade tiles.
 */
const ServiceSearchBar = ({ variant = 'page', initialQuery = '', headingLevel = 'h2' }) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(initialQuery);

  const suggestions = useMemo(() => searchServiceCatalog(inputValue), [inputValue]);
  const note = inputValue.trim();
  const isHero = variant === 'hero';

  const goToSuggestion = (suggestion) => {
    if (suggestion.kind === 'trade') {
      navigate(buildTaskStepPath(suggestion.serviceType));
      return;
    }
    navigate(
      buildTimingStepPath(suggestion.serviceType, suggestion.subServiceType, { from: 'search' }),
    );
  };

  const submitText = () => {
    if (suggestions.length > 0) {
      goToSuggestion(suggestions[0]);
      return;
    }
    if (note) navigate(buildTradeStepPath({ note }));
  };

  const handleChange = (_event, value) => {
    if (!value) return;
    if (typeof value === 'string') {
      submitText();
      return;
    }
    goToSuggestion(value);
  };

  return (
    <SearchBarRoot $variant={variant}>
      {!isHero && (
        <>
          <SearchHeading as={headingLevel}>What do you need help with?</SearchHeading>
          <SearchSubheading>
            See matched Northern Kentucky pros in a couple of taps. No sign-up needed.
          </SearchSubheading>
        </>
      )}

      <SearchFieldRow>
        <Autocomplete
          freeSolo
          autoHighlight
          openOnFocus={false}
          options={suggestions}
          filterOptions={(options) => options}
          inputValue={inputValue}
          onInputChange={(_event, value, reason) => {
            if (reason !== 'reset') setInputValue(value);
          }}
          onChange={handleChange}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          isOptionEqualToValue={(option, value) => option.key === value?.key}
          noOptionsText=""
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <SuggestionOption key={option.key} {...optionProps}>
                <SuggestionIcon aria-hidden>
                  {option.kind === 'trade' ? (
                    <BuildCircleOutlinedIcon />
                  ) : (
                    <ArrowForwardRoundedIcon />
                  )}
                </SuggestionIcon>
                <span>
                  <SuggestionLabel>
                    {option.kind === 'trade' ? `All ${option.label} services` : option.label}
                  </SuggestionLabel>
                  {option.kind === 'subservice' ? (
                    <SuggestionTrade> · {option.tradeLabel}</SuggestionTrade>
                  ) : null}
                </span>
              </SuggestionOption>
            );
          }}
          renderInput={(params) => (
            <SearchInput
              {...params}
              $variant={variant}
              placeholder="Try “clogged drain” or “no heat”"
              inputProps={{
                ...params.inputProps,
                'aria-label': 'What do you need help with?',
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: isHero ? null : (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#5a6478', ml: 0.5 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <SearchSubmitButton $variant={variant} aria-label="Search" onClick={submitText}>
          {isHero ? <SearchRoundedIcon /> : <ArrowForwardRoundedIcon />}
        </SearchSubmitButton>
      </SearchFieldRow>

      {!isHero && <TradeTiles variant="pill" note={note} />}
    </SearchBarRoot>
  );
};

ServiceSearchBar.propTypes = {
  /** `hero` = pill field only (no heading/tiles), sized for the home page photo hero. */
  variant: PropTypes.oneOf(['hero', 'page']),
  /** Prefill (e.g. text kept from an unmatched search). */
  initialQuery: PropTypes.string,
  /** Heading element: `h1` when the bar is the page's main heading. */
  headingLevel: PropTypes.oneOf(['h1', 'h2']),
};

export default ServiceSearchBar;
